// Map from style-dropdown option values → RCMTM factory codes from the RC Code Book.
// Used by getStyleCode() to show the real factory code next to each option
// (e.g. Notch → C-0001) instead of made-up index codes.
//
// Keys are state-field names (e.g. "lapel_type", "vent", "front_closure").
// Values are { displayOption: factoryCode } maps.
// If an option isn't in the map, no code badge is shown.

export const RC_CODE_LOOKUP = {
  // ===== Construction (Canvas) =====
  canvas_construction: {
    "Full Canvas": "C-000A",
    "Half Canvas": "C-000B",
    "Fused": "C-00C1",
    "Medium Fused": "C-00C2",
    "Full Handmade & Full Canvas": "C-OAAA",
    "Half Handmade & Full Canvas": "C-00AA",
    "Full Handmade & Half Canvas": "C-0AAB",
    "Half Handmade & Half Canvas": "C-0BAA",
    "Unconstructed Canvas": "C-0AAD",
    "Full Handmade Unconstructed": "C-00C3",
    "Half Handmade Unconstructed": "C-00AD",
    "Single Chest + Single Shoulder Pad": "C-0001",
    "Single Chest + Regular Shoulder Pad": "C-001T",
  },

  sleeve_pad: {
    "No Sleeve Pad":         "C-001W",
    "Single Layer Pad":      "C-001Y",
  },

  // ===== Jacket =====
  body_style: {
    // RC Code Book has no canvas → body_style mapping; left empty (no badge).
  },

  lapel_type: {
    "Notch": "C-0001",
    "Notch Lapel": "C-0001",
    "Peak": "C-0002",
    "Peak Lapel": "C-0002",
    "Semi Notch": "C-0003",
    "Semi Notch Lapel": "C-0003",
    "Semi Peak": "C-0004",
    "Extended Peak": "C-0004",
    "Semi Peak Lapel": "C-0004",
    "Shawl": "C-0005",
    "Shawl Lapel": "C-0005",
    "Fish": "C-0036",
    "Fish Lapel": "C-0036",
    "Band Collar": "C-0009",
    "Notch + Reverse Slit Peak": "C-005C",
    "Round Peak": "C-00J3",
    "Round Peak Lapel": "C-00J3",
    "Round Peak + Round Collar": "C-00J4",
    "Round Peak Lapel + Round Collar": "C-00J4",
    "Stand Collar w/ Button Tab": "C-001B",
    "Notch + Round Collar Point": "C-001M",
    "Peak + Round Collar Point": "C-0019",
    "Gorge Collar": "C-0018",
    "Notch + High Gorge": "C-0699",
    "Notch + Vertical Satin Middle": "C-0694",
    "Peak + Satin Middle": "C-0692",
    "Peak + Splice Middle": "C-069B",
    "Shoe Lapel w/ Satin": "C-0691",
    // Sport collar variants (tuxedo / specialty)
    "Sport Collar A (Notch + Left Square Notch)": "C-0681",
    "Sport Collar B (Flint Style)": "C-0682",
    "Sport Collar (Notch + Right Square Notch)": "C-0683",
    "Sport Collar C (Flint Style)": "C-0684",
    "Sport Collar D (Notch + Left Sq Notch)": "C-0687",
    "Sport Collar J (Notch + Left Pointed Tab)": "C-0688",
    "27PKN005HAR": "C-0006",
  },

  lapel_width: {
    // Map by approximate value match — dropdown shows e.g. "3in (7.6cm)"
    "2.5in (6.4cm)": "C-00W5",
    "3in (7.6cm)": "C-00W9",
    "3.5in (8.9cm)": "C-00N4",
    "3.75in (9.5cm)": "C-00N6",
    "4in (10.2cm)": "C-00N8",
    "4.5in (11.4cm)": "C-00S3",
    "5in (12.7cm)": "C-00Y9",
    "5.5in (14cm)": "C-00Y8",
    "6in (15.2cm)": "C-00Y8",
  },

  front_closure: {
    "1-Button": "C-0011",
    "2-Button": "C-0012",
    "3-Button": "C-0013",
    "3-Roll-2": "C-002B",
    "4-Button": "C-0014",
    "2x1 DB": "C-002F",
    "4x2 DB": "C-0015",
    "6x1 DB": "C-0018",
    "6x2 DB": "C-0016",
    "8x2 DB": "C-002N",
  },

  vent: {
    "Single Vent": "C-04N1",
    "Center Vent": "C-04N1",
    "Double Vent": "C-04N2",
    "Side Vents": "C-04N2",
    "No Vent": "C-04N0",
  },

  waist_pocket_type: {
    "Flap": "C-0201",
    "Welt": "C-0250",
    "Patch": "C-0260",
    // "Double Besom" → factory code TBD (previously incorrectly mapped to C-0202;
    //  per Richard, C-0202 = "Normal Pocket + Ticket Pocket" combo — merged below)
    "No Pocket": "C-0200",
    // Merged from old "Lower Pocket Combo" dropdown — these are full lower-pocket
    // packages that the factory treats as a single waist-pocket option:
    "Normal Pocket + Ticket Pocket":         "C-0202",
    "Normal Slanted Pocket + Ticket Pocket": "C-02A2",
  },

  waist_pocket_angle: {
    "Straight": "C-0201",
    "Slanted": "C-0210",
  },

  ticket_pocket: {
    "None": "C-0300",
    "Welt": "C-0310",
    "Flap": "C-0301",
    "Patch": "C-0260",
    "Double Besom": "C-0310",
    "Left Side": "C-0302",
    "Right Side": "C-0301",
  },

  chest_pocket: {
    "None": "C-0190",      // assuming Chest Pocket "None"
    "Welt": "C-0250",
    "Patch": "C-0260",
    // "Double Besom" → factory code TBD (see waist_pocket_type note above)
  },

  // Pick-stitching width (visible hand-stitch detail along lapel / edges)
  exterior_stitch: {
    "None": "C-0570",
    "1/16\" Pic": "C-0572",
    "5/16\" Pic": "C-0577",
    // "1/16\" Top", "5/16\" Top", "1/4\" Double Top", "1/4\" Double Pic"
    // → factory codes TBD
  },

  // (Old `lower_pocket_combo` dropdown was merged into `waist_pocket_type` above.)

  lapel_buttonhole: {
    "None": "C-0544",
    "Working Buttonhole": "C-0553",
    "Milanese Hole": "C-0554",
    "Keyhole": "C-0552",
  },

  chest_pocket_posture: {
    "Standard": "C-CP00",
    "+0.5cm (0.2in) Higher": "C-CP01",
    "+1cm (0.4in) Higher": "C-CP02",
    "+1.5cm (0.6in) Higher": "C-CP03",
    "-0.5cm (0.2in) Lower": "C-CP04",
    "-1cm (0.4in) Lower": "C-CP05",
    "-1.5cm (0.6in) Lower": "C-CP06",
  },

  lower_pocket_flap_width: {
    "Standard 5cm (2in)": "C-LF00",
    "4cm (1.6in)": "C-LF01",
    "4.5cm (1.8in)": "C-LF02",
    "5.5cm (2.2in)": "C-LF03",
    "6cm (2.4in)": "C-LF04",
    "6.5cm (2.6in)": "C-LF05",
    "7cm (2.8in)": "C-LF06",
    "7.5cm (3in)": "C-LF07",
    "8cm (3.1in)": "C-LF08",
    "8.5cm (3.3in)": "C-LF09",
    "9cm (3.5in)": "C-LF10",
  },

  ticket_pocket_posture: {
    "Standard": "C-TP00",
    "+0.5cm (0.2in) Higher": "C-TP01",
    "+1cm (0.4in) Higher": "C-TP02",
    "+1.5cm (0.6in) Higher": "C-TP03",
    "+2cm (0.8in) Higher": "C-TP04",
    "-0.5cm (0.2in) Lower": "C-TP05",
    "-1cm (0.4in) Lower": "C-TP06",
    "-1.5cm (0.6in) Lower": "C-TP07",
    "-2cm (0.8in) Lower": "C-TP08",
  },

  back_vent_length: {
    "Standard": "C-0440",
    "+1cm (0.4in)": "C-0441",
    "+2cm (0.8in)": "C-0442",
    "+3cm (1.2in)": "C-0443",
    "+4cm (1.6in)": "C-0444",
    "+5cm (2in)": "C-0445",
    "+6cm (2.4in)": "C-0446",
    "-1cm (0.4in)": "C-0447",
    "-2cm (0.8in)": "C-0448",
    "-3cm (1.2in)": "C-0449",
    "-4cm (1.6in)": "C-044A",
    "-5cm (2in)": "C-044B",
    "-6cm (2.4in)": "C-044C",
  },

  lining_style: {
    "Full": "C-0501",
    "Full Lining": "C-0501",
    "Half": "C-05C9",
    "Half Lining": "C-05C9",
    "Quarter": "C-05P7",
    "Quarter Lining": "C-05P7",
    "Unlined": "C-0403",
    "Piped": "C-0502",
    "Butterfly": "C-0510",
  },

  // ===== Vest =====
  vest_lapel: {
    "None":            "V-4000",  // Standard V Shape - No Collar
    "U Shape":         "V-4001",
    "Shawl":           "V-4002",  // Shawl Lapel w/ Collar Band
    "Shawl No Band":   "V-4003",
    "Notch":           "V-4005",  // Notch Lapel w/ Collar Band
    "Notch No Band":   "V-4004",
    "Peak":            "V-4007",  // Peak Lapel w/ Collar Band
    "Peak No Band":    "V-4006",
    "Semi Notch":      "V-4012",
    "Semi Peak":       "V-4014",
  },

  vest_front_closure: {
    "1-Button":            "V-4117",
    "2-Button":            "V-4116",
    "3-Button":            "V-4110",
    "4-Button":            "V-4111",
    "5-Button":            "V-4112",
    "6-Button":            "V-4113",
    "7-Button":            "V-4114",
    "8-Button":            "V-4115",
    "9-Button":            "V-4118",
    "10-Button":           "V-4119",
    "DB 4-Button":         "V-411A",
    "DB 6-Button":         "V-411B",
    "DB 8-Button":         "V-411C",
    "DB 6x3":              "V-411B",   // 6 buttons total, closest match
  },

  vest_pocket_type: {
    "No Pockets":              "V-4200",
    "No Pocket":               "V-4200",
    "Welt":                    "V-4201",
    "Welt Pocket":             "V-4201",
    "Double Besom":            "V-4202",
    "Double Besom Pocket":     "V-4202",
    "Double Besom w/ Flap":    "V-4203",
    "Double Besom w/ Tab":     "V-4204",
    "Slant Welt":              "V-4210",
    "Slant Double Besom":      "V-4211",
    "Patch":                   "V-4220",
    "Patch w/ Button":         "V-4221",
    "Patch w/ Flap":           "V-4222",
    "Large Patch":             "V-4260",
  },

  vest_pocket_number: {
    "2 Pockets":   "V-4231",   // Left Welt Chest Pocket (single-side ~ 2 lower)
    "4 Pockets":   "V-4234",   // Both Sides Welt (= 4 total with lower 2)
    "No Pockets":  "V-4230",
  },

  vest_chest_pocket: {
    "None":                  "V-4230",
    "Left Welt":             "V-4231",
    "Left Double Besom":     "V-4232",
    "Left Patch":            "V-4233",
    "Both Welt":             "V-4234",
    "Both Double Besom":     "V-4235",
    "Both Patch":            "V-4236",
  },

  vest_back: {
    "Same Fabric":   "V-4300",
    "Lining":        "V-4301",
    "Belt Back":     "V-4310",
    "Elastic":       "V-4311",
    "Belt & Buckle": "V-4312",
    "Martingale":    "V-4313",
    "Cinch":         "V-4314",
    "Pleat":         "V-4320",
  },

  vest_bottom_style: {
    "Pointed":       "V-4400",
    "Straight":      "V-4401",
    "Round":         "V-4402",
    "Square":        "V-4403",
    "Asymmetric":    "V-4404",
    "Extended":      "V-4405",
    "Cutaway":       "V-4406",
  },

  vest_lining_style: {
    "Full":          "V-4600",
    "Half":          "V-4601",
    "Unlined":       "V-4602",
    "Contrast":      "V-4603",
    "Printed":       "V-4604",
  },

  vest_button_style: {
    "Horn":              "V-4700",
    "Corozo":            "V-4701",
    "Mother of Pearl":   "V-4702",
    "Plastic":           "V-4703",
    "Metal":             "V-4704",
    "Fabric Covered":    "V-4705",
    "Contrast":          "V-4706",
  },

  // ===== Shirt =====
  // Standard shirt codes follow the S-1xxx pattern (Kutesmart factory convention).
  // These are not in the RC Code Book file but are the codes RCMTM uses on the order form.
  shirt_collar_style: {
    "Spread":            "S-1001",
    "Cutaway":           "S-1002",
    "Wide Spread":       "S-1003",
    "Semi Spread":       "S-1004",
    "Standard":          "S-1005",
    "Button-Down":       "S-1006",
    "Button Down":       "S-1006",
    "Tab Collar":        "S-1007",
    "Pin Collar":        "S-1008",
    "Club":              "S-1009",
    "Rounded Club":      "S-1009",
    "Band":              "S-100A",
    "Mandarin":          "S-100A",
    "Wing":              "S-100B",
    "Wingtip":           "S-100B",
    "Cuban":             "S-100C",
    "Camp":              "S-100C",
    "Hidden Placket":    "S-100D",
  },

  shirt_collar_height: {
    "Standard 4cm":      "S-1011",
    "3.5cm":             "S-1012",
    "4.5cm":             "S-1013",
    "5cm":               "S-1014",
  },

  shirt_collar_stays: {
    "Removable":         "S-1021",
    "Sewn-In":           "S-1022",
    "No Stays":          "S-1020",
  },

  shirt_cuff_style: {
    "Single Button Barrel":   "S-1101",
    "Two Button Barrel":      "S-1102",
    "Convertible":            "S-1103",
    "French":                 "S-1104",
    "French Cuff":            "S-1104",
    "Mitered Barrel":         "S-1105",
    "Rounded Barrel":         "S-1106",
    "Square Barrel":          "S-1107",
    "Angled Barrel":          "S-1108",
    "Double French":          "S-1104",
  },

  shirt_placket: {
    "Standard":          "S-1201",
    "French Placket":    "S-1202",
    "French":            "S-1202",
    "Hidden Placket":    "S-1203",
    "Hidden":            "S-1203",
    "Pleat Front":       "S-1204",
    "Tuxedo Bib":        "S-1205",
  },

  shirt_pocket: {
    "None":              "S-1300",
    "No Pocket":         "S-1300",
    "Standard":          "S-1301",
    "Standard w/ Flap":  "S-1302",
    "Rounded":           "S-1303",
    "Pointed":           "S-1304",
    "Two Pockets":       "S-1305",
    "Western":           "S-1306",
    "With Pen Slot":     "S-1307",
  },

  shirt_back: {
    "Plain":             "S-1401",
    "Side Pleats":       "S-1402",
    "Box Pleat":         "S-1403",
    "Darts":             "S-1404",
    "Yoke Split":        "S-1405",
  },

  shirt_bottom: {
    "Curved":            "S-1501",
    "Standard Curved":   "S-1501",
    "Straight":          "S-1502",
    "Side Slit":         "S-1503",
  },

  shirt_monogram: {
    "None":              "S-1900",
    "Left Cuff":         "S-1901",
    "Right Cuff":        "S-1902",
    "Chest":             "S-1903",
    "Pocket":            "S-1904",
    "Bottom":            "S-1905",
  },

  shirt_button_style: {
    "Standard":          "S-1700",
    "Mother of Pearl":   "S-1701",
    "Horn":              "S-1702",
    "Plastic":           "S-1703",
    "Contrast Thread":   "S-1710",
  },
  // ===== Trouser =====
  // Body style is sizing fit (Slim / Regular / Classic / Relaxed) — not in the
  // RC Code Book (handled via measurements), so we map to the closest waistband-style code.
  pant_body_style: {
    "Slim": "T-3001",        // Standard pant style — slim/regular/classic all use this base
    "Regular": "T-3001",
    "Classic": "T-3001",
    "Relaxed": "T-3001",
  },

  // Pleats / Front style — full menu from RC Pant Stylebook (Lucy Sun)
  pant_front_style: {
    "Flat Front":                                       "T-3020",
    "Plain Front":                                      "T-3020",
    "Plain Front w/ Double Darts":                      "T-302Y",
    "Plain Front w/ Single Dart":                       "T-302M",
    "Plain Front w/ Side Dart":                         "T-302R",
    "Single Pleat":                                     "T-3021",
    "Single Pleat fwd Sideseam":                        "T-3021",
    "Single Pleat fwd Fly":                             "T-3022",
    "Double Pleat":                                     "T-3023",
    "Double Pleats fwd Fly":                            "T-3023",
    "Double Pleats fwd Sideseam":                       "T-3025",
    "Triple Pleat":                                     "T-3024",
    "Triplicate Pleats fwd Sideseam":                   "T-3024",
    "Triplicate Pleats fwd Fly":                        "T-3026",
    "One Opposite Pleat":                               "T-3027",
    "One Box Pleat":                                    "T-302P",
    "Box Pleat + Sideseam Pleat":                       "T-302J",
    "One Opposite + One Sideseam Pleat":                "T-3028",
    "One Opposite + One Fly Pleat":                     "T-3029",
    "One Sideseam + One Opposite Pleat":                "T-302A",
    "One Fly + One Opposite Pleat":                     "T-302B",
    "Pleats in line w/ Belt Loops":                     "T-302K",
    "Opposite + Double Sideseam Pleats":                "T-302N",
  },

  pant_pleat_depth: {
    "1.5cm":   "T-3027",
    "2.0cm":   "T-302H",
    "2.5cm":   "T-302E",
    "3.2cm":   "T-302F",
    "3.8cm":   "T-302G",
    "4.4cm":   "T-30P2",
  },

  pant_front_pocket: {
    // Slant pockets
    "Slant":                          "T-3101",
    "2.0cm Slant":                    "T-3123",
    "2.5cm Slant":                    "T-3104",
    "3.2cm Slant":                    "T-3124",
    "5.1cm Slant":                    "T-3102",
    "Very Slant":                     "T-3103",
    "5.1 Slant + Welt Ticket":        "T-3107",
    "5.1 Slant + Besom Ticket":       "T-3105",
    "5.1 Slant + Decorative Besom Ticket": "T-3106",
    // Stripe variants
    "Very Slant w/ Stripe":           "T-3100",
    "2.0cm Slant w/ Stripe":          "T-3120",
    "2.5cm Slant w/ Stripe":          "T-3101",
    "3.2cm Slant w/ Stripe":          "T-3121",
    "5.1cm Slant w/ Stripe":          "T-3122",
    // Side-seam / other
    "On-Seam":                        "T-3130",
    "Side Seam":                      "T-3130",
    "Sideseam":                       "T-3130",
    "Double Besom":                   "T-3131",
    "Welt":                           "T-3132",
    "Slant w/ Square Point":          "T-313B",
    "Straight Double Besom":          "T-3139",
    "Straight Double Besom w/ Square Flap": "T-313A",
    "Front Welt w/ Zipper":           "T-313D",
    "Pocket w/ Zipper":               "T-313E",
    "Zipper":                         "T-313E",
    // Jeans pockets
    "Western":                        "T-3133",
    "Square Jean":                    "T-3133",
    "Square Jeans":                   "T-3133",
    "Diamond Jeans":                  "T-3134",
    "Arc Jeans":                      "T-3135",
    "Round Jeans":                    "T-3136",
    "DL Jeans":                       "T-3138",
    "No Pocket":                      "T-313C",
    "No Front Pocket":                "T-313C",
  },

  pant_front_pocket_bartack: {
    "None":                  "T-3190",
    "L Shape":               "T-3191",
    "D Bartack":             "T-3192",
    "Dash (-) Bartack":      "T-3193",
    "X Bartack":             "T-3194",
  },

  pant_front_pocket_stitch: {
    "None":                       "T-31A6",
    "0.15 Top Stitch":             "T-31A1",
    "0.15 Pick Stitch":            "T-31A2",
    "0.3 Top Stitch":              "T-31A8",
    "0.1 + 0.7 Top Stitch":        "T-31A9",
    "0.6 Top Stitch":              "T-31A3",
    "0.6 Pick Stitch":             "T-31A5",
    "0.6cm Wide Pick Stitch":      "T-31A4",
  },

  pant_watch_pocket: {
    "None":                                          "T-3169",
    "Normal Left":                                   "T-3170",
    "Normal Right":                                  "T-3180",
    "Normal Both":                                   "T-3189",
    "Normal 7.6×7.6":                                "T-3179",
    "Besom Left":                                    "T-3171",
    "Welt Right":                                    "T-3181",
    "Double Besom Left":                             "T-3178",
    "Double Besom Right":                            "T-3188",
    "Double Besom Both":                             "T-318J",
    "Left + Diamond Flap":                           "T-3174",
    "Right + Diamond Flap":                          "T-3184",
    "Right + Diamond Flap + Button":                 "T-3186",
    "Right + Triangle Flap":                         "T-3182",
    "Right + Triangle Flap + Button":                "T-3187",
    "Left + Square Flap":                            "T-3177",
    "Right + Square Flap":                           "T-3175",
    "Right + Square Flap + Button":                  "T-3185",
    "Both + Square Flap":                            "T-318K",
    "Double Besom Front + Left Watch":               "T-318T",
    "Opening 10cm":                                  "T-318B",
    "Opening 4.5cm":                                 "T-319F",
  },

  pant_inner_watch_pocket: {
    "None":                              "T-3169",
    "Left":                              "T-3173",
    "Right":                             "T-3183",
    "Both":                              "T-3176",
    "ETQ w/ Zip Inside":                 "T-318U",
    "12cm × 8cm":                        "T-318S",
    "10cm × 10cm":                       "T-318V",
    "7.6cm × 7.6cm":                     "T-318Y",
  },

  pant_back_pocket_style: {
    "No Pocket":                              "T-31A0",
    "No Back Pocket":                         "T-31A0",
    "Double Besom":                           "T-3200",
    "Double Welt":                            "T-3200",   // synonym
    "Double Besom + Button":                  "T-3201",
    "Double Besom + Tab + Button":            "T-3206",
    "Double Besom + Pointed Tab + Button":    "T-3202",
    "Double Besom + Square Tab + Button":     "T-32C1",
    "Double Besom Hidden Button":             "T-320L",
    "Double Besom Lower 1.0 / Upper 0.5":     "T-31A7",
    "Welt":                                   "T-3221",
    "Single Welt":                            "T-3221",
    "Welt + Loop + Button":                   "T-3224",
    "Welt + Zipper":                          "T-320P",
    "Besom Back":                             "T-3220",
    "Back w/ Flap":                           "T-3210",
    "Back w/ Diamond Flap":                   "T-3212",
    "Back w/ Diamond Flap + Button":          "T-3213",
    "Back w/ Pointed Flap":                   "T-3214",
    "Back w/ Pointed Flap + Button":          "T-32C4",
    "Back w/ Semi-Point Flap":                "T-32C5",
    "Back w/ Semi-Point Flap + Button":       "T-32C6",
    "Knife Shape + Button":                   "T-32C3",
    "Pointed Patch":                          "T-3230",
    "Pointed Patch + Button":                 "T-3231",
    "Round Patch":                            "T-3232",
    "Round Patch + Button":                   "T-3233",
    "Patch":                                  "T-3230",
    "Flap":                                   "T-3210",
    "Back Zipper":                            "T-3205",
    "Zipper":                                 "T-3205",
    "Double Welt + Tab + Button (in pocket)": "T-32PA",
    "Double Welt L + Double Welt R":          "T-3211",
  },

  pant_back_pocket_closure: {
    "Button":          "T-3201",  // Double Besom w/ Button
    "No Button":       "T-3200",  // Double Besom - No Buttons
    "Tab & Button":    "T-3206",
    "Pointed Tab & Button":  "T-3202",
    "Square Tab & Button":   "T-32C1",
    "Round Tab & Button":    "T-32D4",
    "Triangle Tab & Button": "T-32D6",
    "Hidden Button":   "T-320L",
    "Zipper":          "T-3205",
    "Loop & Button":   "T-3224",
  },

  pant_bottom_finishing: {
    "Plain Hem":       "T-3600",
    "Cuff 1.5\"":      "T-3603",  // 3.8cm cuff (~1.5in)
    "Cuff 1.75\"":     "T-3604",  // 4.4cm cuff (~1.75in)
    "Cuff 2\"":        "T-3605",  // 5.0cm cuff (~2in)
    "1.25\" Cuff":     "T-3602",  // 3.2cm cuff
    "No Finish":       "T-360A",
    "Inside Cuff":     "T-360D",
  },

  pant_front_closure: {
    "Button & Tab":    "T-3401",  // Straight Fly
    "Hook & Bar":      "T-3400",  // Fish-Mouth Fly (no separate code; closest)
    "Extended Tab":    "T-340E",  // Point Trouser Inner Fly Extension
    "French Fly":      "T-3403",
    "Texas Fly":       "T-3408",
  },

  pant_waistband_style: {
    "Standard":                          "T-3010",
    "Front & Back Continuous":           "T-3011",
    "Uncontinued Front + Continued Back": "T-3012",
    "Front Continued":                   "T-3013",
    "Extended":                          "T-3013",
    "Side Tabs":                         "T-33T7",  // Side Tabs on Waistband
    "Belt Loops":                        "T-3330",  // Regular Belt Loops (closest belt-loop ref)
  },

  pant_waistband_width: {
    "3.5cm (1.4in)":   "T-301C",
    "3.8cm (1.5in)":   "T-301E",
    "4.0cm (1.6in)":   "T-301D",
    "4.5cm (1.8in)":   "T-3015",
    "5.0cm (2.0in)":   "T-301F",
  },

};

/**
 * Returns the RCMTM factory code for a given style option, or "" if not mapped.
 * fieldName is the state-field key used in styles (e.g. "lapel_type", "vent").
 */
export function getRcCode(fieldName, value) {
  if (!fieldName || !value) return "";
  const map = RC_CODE_LOOKUP[fieldName];
  if (!map) return "";
  return map[value] || "";
}
